import * as errors from '@attain-apps/errors'
import * as rxjs from 'rxjs'

import type { Action } from '../action'
import { Phase } from '../phase'

import type { OpManager } from './op-manager'

/**
 * Number of milliseconds after which, if no instruction is received, the relay
 * errors out
 *
 * This should primarily happen only when an effect forgets to close the
 * instruction observable given to `relay`. In most cases, this can be done with
 * {@link rxjs.take}(1)
 */
const INSTRUCTION_TIMEOUT_MS = 30e3

/**
 * Number of milliseconds after which, if no response is received for an inner
 * request, the inner request errors out
 */
const INNER_RESPONSE_TIMEOUT_MS = 60e3

/**
 * When it comes to listening for follow up actions, we don't want to forever
 * listen to follow ups for every request, as that will quickly become
 * excessive. So we limit the number of outer requests that we actively listen
 * to for follow up actions
 */
const MAX_FOLLOW_UP_SUBSCRIPTIONS = 10

export function createRelay<
  TRequestGroup extends OpManager.RequestGroup<
    any,
    string,
    unknown,
    unknown,
    unknown,
    unknown
  >,
>(
  requestManager: TRequestGroup,
  mapper: Parameters<TRequestGroup['relay']>[0],
  options: OpManager.RequestGroup.relay.Options = { strategy: 'merge' },
): rxjs.Observable<Action> {
  const flatMap =
    options?.strategy === 'switch' ? rxjs.switchMap : rxjs.mergeMap
  let activeFollowUpSubscribers: {
    outerRequestStartTime: number
    subscriber: rxjs.Subscriber<Action>
  }[] = []
  const action$ = requestManager.request.observe().pipe(
    flatMap(
      outerRequestAction =>
        new rxjs.Observable<Action>(subscriber => {
          const outerRequestStartTime = Date.now()
          /* -------------------------- Response aggregation -------------------------- */
          const request = outerRequestAction.data
          let accumulatedResponse: unknown
          let firstCancellationReason: string | undefined
          let accumulatedError: Error | undefined
          function addError(error: Error) {
            if (accumulatedError) {
              if (
                errors.UserFriendlyAggregateError.isUserFriendlyAggregateError(
                  accumulatedError,
                )
              ) {
                // If we already have an aggregate error,
                // we can just add this error
                accumulatedError.addError(error)
              } else {
                // Otherwise, if this is the second error,
                // we need to convert to an aggregate
                // error
                accumulatedError = new errors.UserFriendlyAggregateError([
                  accumulatedError,
                  error,
                ])
              }
            } else {
              // If this is the first error, we just use
              // this error (but we standardize
              // AggregateError into
              // UserFriendlyAggregateError)
              accumulatedError =
                error instanceof AggregateError
                  ? new errors.UserFriendlyAggregateError(error)
                  : error
            }
          }
          /**
           * Creates the outer response based on the aggregated results of the
           * inner requests
           */
          function createOuterResponse(): Action {
            if (accumulatedError !== undefined) {
              // If we have an inner error, we emit an outer error
              return requestManager.error.from(outerRequestAction).create({
                error: accumulatedError,
                request,
              })
            } else if (firstCancellationReason !== undefined) {
              // Otherwise, if we have an inner cancellation, we
              // emit an outer cancellation
              return requestManager.cancellation
                .from(outerRequestAction)
                .create({
                  reason: firstCancellationReason,
                  request,
                })
            } else {
              // Otherwise, we emit an outer success
              return requestManager.success.from(outerRequestAction).create({
                request,
                response: accumulatedResponse,
              })
            }
          }

          /* ------------------------- Subscription management ------------------------ */
          const innerSubscriptions: rxjs.Subscription[] = []
          let noIncomingInstructions = false
          let numberOfInnerRequestsWithoutResponses = 0
          let hasSentOuterResponse = false

          function submitOuterResponseIfReady(): void {
            if (
              !hasSentOuterResponse &&
              noIncomingInstructions &&
              numberOfInnerRequestsWithoutResponses === 0
            ) {
              subscriber.next(createOuterResponse())
              hasSentOuterResponse = true
              // We have officially switched to follow up actions, and thus need
              // to be tracked as a follow up subscriber
              activeFollowUpSubscribers.push({
                outerRequestStartTime,
                subscriber,
              })
              if (
                activeFollowUpSubscribers.length > MAX_FOLLOW_UP_SUBSCRIPTIONS
              ) {
                // If we now have too many follow up subscribers, we first get
                // rid of any subscribers that have since finished
                activeFollowUpSubscribers = activeFollowUpSubscribers.filter(
                  item => !item.subscriber.closed,
                )
                if (
                  activeFollowUpSubscribers.length > MAX_FOLLOW_UP_SUBSCRIPTIONS
                ) {
                  // If we still have too many, we forcibly close the earliest
                  // in the list. To determine that, we first have to sort the
                  // list by request time
                  activeFollowUpSubscribers.sort(
                    (a, b) => a.outerRequestStartTime - b.outerRequestStartTime,
                  )
                  activeFollowUpSubscribers.shift()?.subscriber.complete()
                }
              }
            }
          }

          /* -------------------------- Instruction execution ------------------------- */
          const instruction$ = mapper(
            (target, mapping) => ({
              forward: { ...mapping, target },
            }),
            outerRequestAction.data,
            outerRequestAction,
          ).pipe(
            rxjs.timeout({
              each: INSTRUCTION_TIMEOUT_MS,
            }),
          )
          innerSubscriptions.push(
            instruction$.subscribe({
              complete: () => {
                noIncomingInstructions = true
                submitOuterResponseIfReady()
              },
              error: (error: Error) => {
                noIncomingInstructions = true
                // NOTE: addError must be called before
                // submitOuterResponseIfReady so that the error is included in
                // the outer response
                addError(error)
                submitOuterResponseIfReady()
              },
              next: instruction => {
                /* --------------------------- Immediate response --------------------------- */
                if (instruction.success) {
                  hasSentOuterResponse = true
                  // Immediate success
                  subscriber.next(
                    requestManager.success
                      .from(outerRequestAction)
                      .create(
                        instruction.success === true
                          ? { request, response: undefined }
                          : { ...instruction.success, request },
                      ),
                  )
                  return
                } else if (instruction.error) {
                  hasSentOuterResponse = true
                  // Immediate error
                  subscriber.next(
                    requestManager.error
                      .from(outerRequestAction)
                      .create(
                        instruction.error === true
                          ? { request }
                          : { ...instruction.error, request },
                      ),
                  )
                  return
                } else if (instruction.cancellation) {
                  hasSentOuterResponse = true
                  // Immediate cancellation
                  subscriber.next(
                    requestManager.cancellation.from(outerRequestAction).create(
                      instruction.cancellation === true
                        ? { request }
                        : {
                            ...instruction.cancellation,
                            request,
                          },
                    ),
                  )
                  return
                }

                /* -------------------------------- Forwards -------------------------------- */
                const forwards =
                  // If there is a sequence, we use that
                  instruction.sequence?.map(({ forward }) => forward) ??
                  // Otherwise, if there is a single forward
                  // request, we use that
                  (instruction.forward && [instruction.forward]) ??
                  // Finally, if we have no valid instructions, we
                  // just use an empty list
                  []
                if (!forwards.length) {
                  // If for whatever reason, we don't end up with
                  // any valid instructions, then there is nothing
                  // we can do but ignore it and hope there are
                  // other valid instructions in the stream
                  return
                }
                numberOfInnerRequestsWithoutResponses += forwards.length
                let nextForwardIdx = 0
                function executeNextForward() {
                  if (nextForwardIdx >= forwards.length) {
                    // We've reached the end of the list
                    return
                  }
                  const forward = forwards[nextForwardIdx]
                  nextForwardIdx++
                  // If no immediate result was emitted, then we do the
                  // more standard request forwarding
                  const {
                    input,
                    output,
                    target,
                    onCancellation,
                    onError,
                    onResponse,
                    onSuccess,
                  } = forward
                  const relayTarget = target as OpManager.RequestGroup<
                    any,
                    string,
                    unknown,
                    unknown,
                    unknown,
                    unknown
                  >
                  const innerRequestAction = relayTarget.request
                    .from(outerRequestAction)
                    .create(input)

                  let innerRequestHasResponse = false
                  innerSubscriptions.push(
                    rxjs
                      .merge(
                        // Success
                        relayTarget.success.observe().pipe(
                          innerRequestAction.relatedBy({ request: true }),
                          rxjs.tap(innerSuccessAction => {
                            const { data } = innerSuccessAction
                            // Simply accumulates a response
                            accumulatedResponse = output?.(
                              data,
                              accumulatedResponse,
                            )
                            onSuccess?.(
                              data,
                              accumulatedResponse,
                              innerSuccessAction,
                            )
                            // Now that we have successfully
                            // completed this request, we can
                            // execute the next one in the
                            // sequence, if there is one
                            executeNextForward()
                            // NOTE: the above line must happen
                            // before we call `innerResponseDone`
                            // so that `finishIfReady` knows that
                            // there is another forward being
                            // executed
                          }),
                        ),
                        // Error
                        relayTarget.error.observe().pipe(
                          innerRequestAction.relatedBy({ request: true }),
                          rxjs.tap(innerErrorAction => {
                            const { error } = innerErrorAction.data
                            if (!hasSentOuterResponse) {
                              addError(error)
                            }
                            // NOTE: because of AllowMissing and
                            // `any`, we end up in this situation
                            // where TypeScript thinks there is a
                            // problem of optional properties that
                            // should be required. The simplest way
                            // to fix this was just to pretend that
                            // the action has no partial properties
                            onError?.(
                              error,
                              innerErrorAction as Required.Deep<
                                typeof innerErrorAction
                              >,
                            )
                          }),
                        ),
                        // Cancellation
                        relayTarget.cancellation.observe().pipe(
                          innerRequestAction.relatedBy({ request: true }),
                          rxjs.tap(innerCancellationAction => {
                            const { reason } = innerCancellationAction.data
                            if (!hasSentOuterResponse) {
                              // Simply records the first cancellation
                              firstCancellationReason ??= reason
                            }
                            // NOTE: because of AllowMissing and
                            // `any`, we end up in this situation
                            // where TypeScript thinks there is a
                            // problem of optional properties that
                            // should be required. The simplest way
                            // to fix this was just to pretend that
                            // the action has no partial properties
                            onCancellation?.(
                              reason,
                              innerCancellationAction as Required.Deep<
                                typeof innerCancellationAction
                              >,
                            )
                          }),
                        ),
                      )
                      .pipe(
                        rxjs.timeout({
                          first: INNER_RESPONSE_TIMEOUT_MS,
                        }),
                      )
                      .subscribe({
                        complete: () => {
                          if (!innerRequestHasResponse) {
                            // If we haven't responded already, we aren't going
                            // to get one, so we just consider it complete
                            numberOfInnerRequestsWithoutResponses--
                            submitOuterResponseIfReady()
                          }
                        },
                        error: (error: Error) => {
                          if (!innerRequestHasResponse) {
                            // If we haven't responded already, we aren't going
                            // to get one, so we just consider it complete
                            numberOfInnerRequestsWithoutResponses--
                            addError(error)
                            // NOTE: this must happen after
                            // `addError` since that will be used in
                            // `innerResponseDone`
                            submitOuterResponseIfReady()
                          }
                        },
                        next: action => {
                          if (hasSentOuterResponse) {
                            // If we already submitted an outer response,
                            // which happens after every inner request already
                            // has a response, then we are creating a follow
                            // up action. In that case, we mostly just try to
                            // match the inner follow up action with a
                            // corresponding outer follow up action
                            switch (action.op.status) {
                              case 'cancellation':
                                subscriber.next(
                                  requestManager.cancellation
                                    .from(outerRequestAction)
                                    .create({
                                      reason:
                                        // NOTE: TypeScript doesn't realize that
                                        // the switch-case on the
                                        // action.op.status tells us what the
                                        // data should look like
                                        (
                                          action.data as FlattenUnion<
                                            typeof action.data
                                          >
                                        ).reason,
                                      request,
                                    }),
                                )
                                break
                              case 'error':
                                subscriber.next(
                                  requestManager.error
                                    .from(outerRequestAction)
                                    .create({
                                      error:
                                        // NOTE: TypeScript doesn't realize that
                                        // the switch-case on the
                                        // action.op.status tells us what the
                                        // data should look like
                                        (
                                          action.data as FlattenUnion<
                                            typeof action.data
                                          >
                                        ).error,
                                      request,
                                    }),
                                )
                                break
                              case 'success':
                                subscriber.next(
                                  requestManager.success
                                    .from(outerRequestAction)
                                    .create({
                                      request,
                                      response: accumulatedResponse,
                                    }),
                                )
                                break
                            }
                          } else if (action.phase === Phase.Response) {
                            // If this is a response, then we can mark this
                            // request as responded
                            innerRequestHasResponse = true
                            // And mark that we are one step closer to having
                            // all our inner responses
                            numberOfInnerRequestsWithoutResponses--
                            // And in case this was the last inner response
                            // needed, we see if we are ready to submit the
                            // outer response
                            submitOuterResponseIfReady()
                            // NOTE: we do this last in case an error is thrown
                            onResponse?.()
                          }
                        },
                      }),
                  )

                  // Now that our result listener/subscription is set
                  // up, we can emit the request
                  subscriber.next(innerRequestAction)
                }
                // Starts us off by executing the first forward in
                // the sequence
                executeNextForward()
              },
            }),
          )

          /* ----------------------- Request processing clean up ---------------------- */
          return () => {
            for (const innerSubscription of innerSubscriptions) {
              innerSubscription.unsubscribe()
            }
          }
        }),
    ),
  )
  return action$
}
